import React, { memo, useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebaseConfig";
import { signOut } from "firebase/auth";
import { useUser } from "../context/UserContext";
import { useLanguage } from "../context/LanguageContext";
import { 
  FaBookOpen, FaSignOutAlt, FaBell, 
  FaBars, FaTimes, 
  FaUserGraduate, FaGlobe, FaChartLine,
  FaTrash, FaArrowRight, FaUser, FaCog
} from "react-icons/fa";
import { 
  doc, 
  collection,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  deleteDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
  onSnapshot
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getMessaging, onMessage, isSupported } from "firebase/messaging";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { format } from 'date-fns';
import { enGB, ar } from 'date-fns/locale';

const Navbar = memo(() => {
  const { user, setUser, userData } = useUser();
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const profileRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  const navItems = [
    { path: "/", label: language === 'ar' ? "الرئيسية" : "Home", icon: null },
    { path: "/courses", label: language === 'ar' ? "الدورات" : "Courses" },
    { 
      path: "/my-learning", 
      label: language === 'ar' ? "الاشتراكات" : "My Learning",
    },
    { 
      path: "/study-progress", 
      label: language === 'ar' ? "تقويم المهام" : "Tasks Calendar",
    },
  ];

  const dateLocale = language === 'ar' ? ar : enGB;

  const formatNotificationDate = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const notificationDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInHours = (now - notificationDate) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return language === 'ar' ? 
        `منذ ${diffInMinutes > 0 ? diffInMinutes : 1} دقيقة` : 
        `${diffInMinutes > 0 ? diffInMinutes : 1} min ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return language === 'ar' ? 
        `منذ ${hours} ساعة` : 
        `${hours} hr ago`;
    } else {
      const days = Math.floor(diffInHours / 24);
      if (days === 1) {
        return language === 'ar' ? 'أمس' : 'Yesterday';
      } else if (days < 7) {
        return language === 'ar' ? 
          `منذ ${days} أيام` : 
          `${days} days ago`;
      } else {
        return format(notificationDate, 'MMM d', { locale: dateLocale });
      }
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (!user?.uid) return;
    
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(q);
      const notificationsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          body: data.body || data.message,
          read: data.read || data.isRead || false,
          createdAt: data.createdAt,
          data: data.data || {},
          formattedDate: formatNotificationDate(data.createdAt)
        };
      });

      setNotifications(notificationsData);
      setUnreadCount(notificationsData.filter(n => !n.read).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [user, language]);

  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedNotifications = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          body: data.body || data.message,
          read: data.read || data.isRead || false,
          createdAt: data.createdAt,
          data: data.data || {},
          formattedDate: formatNotificationDate(data.createdAt)
        };
      });
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter(n => !n.read).length);
    });

    return () => unsubscribe();
  }, [user?.uid, language]);

  useEffect(() => {
    const setupMessaging = async () => {
      try {
        const messagingSupported = await isSupported();
        if (!messagingSupported) return;

        const messaging = getMessaging();
        const unsubscribe = onMessage(messaging, (payload) => {
          toast.custom((t) => (
            <div className={`bg-white p-4 rounded-lg shadow-lg border-l-4 ${language === 'ar' ? 'border-r-4 border-l-0' : ''} border-blue-500`}
                 dir={language === 'ar' ? 'rtl' : 'ltr'}>
              <h3 className="font-bold text-gray-800">{payload.notification?.title}</h3>
              <p className="text-gray-600">{payload.notification?.body}</p>
              <div className="flex justify-end mt-2">
                <button 
                  onClick={() => {
                    if (payload.data?.courseId) {
                      navigate(`/course/${payload.data.courseId}`);
                    }
                    toast.dismiss(t.id);
                  }} 
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                </button>
              </div>
            </div>
          ));
          fetchNotifications();
        });

        return unsubscribe;
      } catch (error) {
        console.error('Messaging error:', error);
      }
    };

    setupMessaging();
  }, [language, navigate, fetchNotifications]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsProfileOpen(false);
      setIsMobileMenuOpen(false);
      navigate("/");
      toast.success(language === 'ar' ? "تم تسجيل الخروج بنجاح" : "Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error(language === 'ar' ? "حدث خطأ أثناء تسجيل الخروج" : "Error during logout");
    }
  }, [language, navigate, setUser]);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!user?.uid || notifications.length === 0) return;
    
    try {
      const batch = writeBatch(db);
      
      notifications
        .filter(n => !n.read)
        .forEach(notification => {
          const notificationRef = doc(db, 'notifications', notification.id);
          batch.update(notificationRef, { 
            read: true,
            isRead: true,
            readAt: serverTimestamp()
          });
        });
      
      await batch.commit();
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  }, [notifications, user]);

  const toggleNotifications = useCallback(() => {
    setIsNotificationsOpen(prev => {
      if (!prev && unreadCount > 0) {
        markAllNotificationsAsRead();
      }
      return !prev;
    });
  }, [unreadCount, markAllNotificationsAsRead]);

  const markSingleNotificationAsRead = useCallback(async (notificationId) => {
    if (!user?.uid) return;
    
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        isRead: true,
        readAt: serverTimestamp()
      });
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }, [user]);

  const deleteNotification = useCallback(async (notificationId) => {
    if (!user?.uid) return;
    
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      toast.success(language === 'ar' ? 'تم حذف الإشعار' : 'Notification deleted');
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error(language === 'ar' ? 'حدث خطأ في حذف الإشعار' : 'Error deleting notification');
    }
  }, [user, language]);

  const handleNotificationClick = useCallback((notification) => {
    if (notification.data?.courseId) {
      navigate(`/course/${notification.data.courseId}`);
    }
    setIsNotificationsOpen(false);
    if (!notification.read) {
      markSingleNotificationAsRead(notification.id);
    }
  }, [navigate, markSingleNotificationAsRead]);

  const handleClickOutside = useCallback((event) => {
    if (profileRef.current && !profileRef.current.contains(event.target)) {
      setIsProfileOpen(false);
    }
    if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
      setIsMobileMenuOpen(false);
    }
    if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
      setIsNotificationsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const toggleProfile = useCallback(() => setIsProfileOpen(prev => !prev), []);
  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(prev => !prev), []);

  const getUserDisplayName = () => {
    if (user?.displayName) return user.displayName;
    if (userData?.name) return userData.name;
    return language === 'ar' ? "مستخدم" : "User";
  };

  const getUserEmail = () => {
    if (user?.email) return user.email;
    return language === 'ar' ? "بريد إلكتروني غير معروف" : "Unknown email";
  };

  const getUserInitial = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  // إضافة وظيفة لمنع تمرير الصفحة عند فتح القائمة المحمولة
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav className="bg-white shadow-sm sticky top-0 z-50 font-sans border-b border-gray-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left section - Logo and mobile menu */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={toggleMobileMenu} 
                className="lg:hidden text-gray-600 hover:text-gray-900 p-2 rounded-md transition-colors touch-manipulation active:scale-95"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isMobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
              </button>
              <Link to="/" className="flex items-center">
                <img 
                  src="/SWIFT_Logo.png" 
                  alt="SWIFT Academy Logo" 
                  className="h-12 w-[180px] object-contain"
                  style={{ minWidth: '10px' }}
                />
              </Link>
            </div>

            {/* Right section - Navigation items and user menu */}
            <div className="flex items-center space-x-4">
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname === item.path
                        ? "bg-green-100 text-green-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Language toggle */}
              <button 
                onClick={toggleLanguage} 
                className="p-2.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-200 touch-manipulation active:scale-95"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                title={language === 'ar' ? 'Switch to English' : 'التغيير إلى العربية'}
              >
                <FaGlobe className="text-lg" />
              </button>

              {/* Notifications */}
              {user && (
                <div className="relative" ref={notificationsRef}>
                  <button 
                    onClick={toggleNotifications} 
                    className="p-2.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-200 relative touch-manipulation active:scale-95"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title={language === 'ar' ? 'الإشعارات' : 'Notifications'}
                  >
                    <FaBell className="text-lg" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium shadow-sm animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {isNotificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className={`absolute ${language === 'ar' ? 'left-0' : 'right-0'} mt-2 w-80 sm:w-96 bg-white shadow-2xl rounded-xl z-50 border border-gray-200`}
                    >
                      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="font-semibold text-gray-800">
                          {language === 'ar' ? 'الإشعارات' : 'Notifications'}
                        </h3>
                        {unreadCount > 0 && (
                          <button 
                            onClick={markAllNotificationsAsRead}
                            className="text-sm text-blue-600 hover:text-blue-800 transition-colors touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {language === 'ar' ? 'تعليم الكل كمقروء' : 'Mark all read'}
                          </button>
                        )}
                      </div>
                      
                      <div className="max-h-96 overflow-y-auto overscroll-contain">
                        {notifications.length === 0 ? (
                          <div className="p-6 text-center text-gray-500">
                            <FaBell className="text-3xl text-gray-300 mx-auto mb-3" />
                            <p>{language === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}</p>
                          </div>
                        ) : (
                          <ul className="divide-y divide-gray-100">
                            {notifications.slice(0, 5).map((notification) => (
                              <motion.li
                                key={notification.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                                className={`p-4 hover:bg-gray-50 cursor-pointer transition-all duration-200 touch-manipulation active:bg-gray-100 ${
                                  !notification.read ? 'bg-blue-50' : ''
                                }`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-medium truncate ${
                                      notification.read ? 'text-gray-700' : 'text-gray-900'
                                    }`}>
                                      {notification.title}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                      {notification.body}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-2">
                                      {notification.formattedDate}
                                    </p>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteNotification(notification.id);
                                    }}
                                    className="ml-2 text-gray-400 hover:text-red-500 p-1 transition-colors flex-shrink-0 touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                    title={language === 'ar' ? 'حذف' : 'Delete'}
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              </motion.li>
                            ))}
                          </ul>
                        )}
                      </div>
                      
                      <div className="p-4 border-t border-gray-200">
                        <Link 
                          to="/notifications" 
                          className="inline-flex items-center justify-center w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-all duration-200 touch-manipulation active:bg-green-800"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          onClick={() => setIsNotificationsOpen(false)}
                        >
                          {language === 'ar' ? 'عرض جميع الإشعارات' : 'View All Notifications'}
                          <FaArrowRight className={`ml-2 ${language === 'ar' ? 'transform rotate-180' : ''}`} />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* User Profile */}
              {user ? (
                <div className="relative" ref={profileRef}>
                  <button 
                    onClick={toggleProfile} 
                    className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 transition-all duration-200 touch-manipulation active:scale-95"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title={language === 'ar' ? 'الملف الشخصي' : 'Profile'}
                  >
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-semibold shadow-sm">
                      {getUserInitial()}
                    </div>
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className={`absolute ${language === 'ar' ? 'left-0' : 'right-0'} mt-2 w-64 bg-white shadow-2xl rounded-xl z-50 border border-gray-200`}
                    >
                      {/* User Info */}
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-semibold text-lg shadow-sm">
                            {getUserInitial()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {getUserDisplayName()}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {getUserEmail()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="p-2">
                        <Link 
                          to="/profile" 
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center px-3 py-2.5 text-gray-700 hover:bg-gray-100 rounded-md text-sm font-medium transition-all duration-200 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <FaUser className={language === 'ar' ? "ml-2" : "mr-2"} />
                          {language === 'ar' ? "الملف الشخصي" : "Profile"}
                        </Link>
                        <Link 
                          to="/my-learning" 
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center px-3 py-2.5 text-gray-700 hover:bg-gray-100 rounded-md text-sm font-medium transition-all duration-200 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <FaBookOpen className={language === 'ar' ? "ml-2" : "mr-2"} />
                          {language === 'ar' ? "الاشتراكات" : "My Courses"}
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="p-2 border-t border-gray-200">
                        <button 
                          onClick={handleLogout}
                          className="flex items-center w-full px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium transition-all duration-200 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <FaSignOutAlt className={language === 'ar' ? "ml-2" : "mr-2"} />
                          {language === 'ar' ? "تسجيل الخروج" : "Logout"}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              ) : (
                <Link 
                  to="/login" 
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm font-medium whitespace-nowrap shadow-sm transition-all duration-200 touch-manipulation active:bg-green-800"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {language === 'ar' ? "تسجيل الدخول" : "Login"}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={toggleMobileMenu} />
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, x: language === 'ar' ? 100 : -100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: language === 'ar' ? 100 : -100 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          ref={mobileMenuRef}
          className={`lg:hidden fixed top-16 ${language === 'ar' ? 'right-0' : 'left-0'} h-[calc(100vh-64px)] w-80 bg-white shadow-2xl z-50 overflow-y-auto overscroll-contain`}
          dir={language === 'ar' ? 'rtl' : 'ltr'}
        >
          <div className="px-4 py-6">
            {/* User Info */}
            {user && (
              <div className="flex items-center p-4 mb-6 bg-gradient-to-r from-green-50 to-gray-50 rounded-xl shadow-sm border border-gray-100">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-semibold text-xl shadow-sm">
                  {getUserInitial()}
                </div>
                <div className={language === 'ar' ? "mr-4" : "ml-4"}>
                  <p className="text-sm font-semibold text-gray-900">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {getUserEmail()}
                  </p>
                </div>
              </div>
            )}

            {/* Language Toggle */}
            <button 
              onClick={() => {
                toggleLanguage();
                setIsMobileMenuOpen(false);
              }} 
              className="flex items-center w-full px-4 py-3 mb-4 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition-all duration-200 touch-manipulation active:bg-gray-100"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <FaGlobe className={`${language === 'ar' ? "ml-3" : "mr-3"} text-green-600`} />
              {language === 'ar' ? "English" : "العربية"}
            </button>

            {/* Navigation Items */}
            <div className="space-y-1 mb-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 touch-manipulation ${
                    location.pathname === item.path
                      ? "bg-green-100 text-green-700 shadow-sm"
                      : "text-gray-600 hover:bg-gray-50 active:bg-gray-100"
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.icon && <span className={`${language === 'ar' ? 'ml-3' : 'mr-3'} text-green-600`}>{item.icon}</span>}
                  <span>{item.label}</span>
                </Link>
              ))}

              {user && (
                <>
                  <Link
                    to="/notifications"
                    className="flex items-center px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition-all duration-200 touch-manipulation active:bg-gray-100"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaBell className={`${language === 'ar' ? "ml-3" : "mr-3"} text-green-600`} />
                    <span>
                      {language === 'ar' ? "الإشعارات" : "Notifications"}
                    </span>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-auto shadow-sm animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition-all duration-200 touch-manipulation active:bg-gray-100"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaUser className={`${language === 'ar' ? "ml-3" : "mr-3"} text-green-600`} />
                    <span>
                      {language === 'ar' ? "الملف الشخصي" : "Profile"}
                    </span>
                  </Link>
                </>
              )}
            </div>

            {/* Action Buttons */}
            {user ? (
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl text-sm font-medium transition-all duration-200 touch-manipulation active:bg-red-100"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <FaSignOutAlt className={`${language === 'ar' ? "ml-2" : "mr-2"}`} />
                  <span>
                    {language === 'ar' ? "تسجيل الخروج" : "Logout"}
                  </span>
                </button>
              </div>
            ) : (
              <div className="pt-4 border-t border-gray-200">
                <Link
                  to="/login"
                  className="block w-full text-center bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700 font-medium shadow-sm transition-all duration-200 touch-manipulation active:bg-green-800"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {language === 'ar' ? "تسجيل الدخول" : "Login"}
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </>
  );
});

export default Navbar;